/**
 * NewBookingFlowContent.jsx
 * Module: common / PaymentSection
 *
 * Booking confirmation / checkout screen for a selected air-cargo rate.
 * Renders the flight summary, the Payment Summary breakdown (Taxable Amount,
 * GST Amount, Net Amount), the goods description and the AWB-type picker,
 * then drives the CashFree payment flow through to booking creation
 * (requestType "1") or a booking update (requestType "2").
 *
 * Author: FreightSee frontend team
 * Date: 2026-07-06
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import CategoryIcon from "@mui/icons-material/Category";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { airport_list } from "../../../store/Admin/Common/CommonSlice";
import {
  booking,
  payment_verification,
  lattao,
} from "../../../store/BookingIntermediaterypage/airlineairportslice";
// NOTE: payment_failure (QueriesSlice' `paymentfailed`) is deliberately NOT
// imported — that endpoint is no longer called from anywhere in the app.
import { getorderid, bookingStart } from "../../../store/Queries/QueriesSlice";
import { formatrates } from "../../../functions/Utils";
import {
  updateStoredSearch,
  clearStoredSearch,
} from "../../../store/Newsearchform/Newsearchform";
import { updatenotificationdata } from "../../../store/Admin/Common/CommonSlice";
import { updatebookinghistorytabvalue } from "../../../store/Common/CommonSlice";
import {
  viewBreakUp,
  clearstate,
  searchratestate,
} from "../../../store/SearchRates/SearchRatesSlice";
import { buildRateSearchPayload } from "../../../utils/buildRateSearchPayload";
import { getEkRateQuotationID } from "../../../utils/ekRate";
import { formatTemperatureRange } from "../BookingDetailUI";
import { selectStoredBookingId, clearBookingIdStore } from "../../../store/Newslices/bookingIdSlice";
import { selectStoredQueryId, clearQueryIdStore } from "../../../store/Newslices/queryIdSlice";
import { selectPrevBookingAmount, clearPrevBookingAmountStore } from "../../../store/Newslices/prevBookingAmountSlice";
import { selectStoredAwbNumber } from "../../../store/Newslices/awbSlice";
import { bookingupdate, remaining_balance_update } from "../../../store/BookingIntermediaterypage/airlineairportslice";
import { getsinglebookingdata } from "../../../store/BookingHistory/BookingHistorySlice";
import { get_single_search_query } from "../../../store/Admin/Queries/QuerySlice";
import { cashfreeSandbox } from "cashfree-pg-sdk-javascript";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { httpinstance } from "../../../axios/api";

countries.registerLocale(enLocale);

const getFlagUrl = (countryName) => {
  if (!countryName) return null;
  const code = countries.getAlpha2Code(countryName.trim(), "en");
  return code ? `https://flagcdn.com/w40/${code.toLowerCase()}.png` : null;
};

// auto_book / auto_search arrive as 0/1, "0"/"1" or null.
const isFlagOn = (flag) => Number(flag) === 1;
const normName = (v) => String(v ?? "").trim().toLowerCase();

/**
 * isAutoBookCarrier
 * True when the selected rate's carrier (matched on carr_name / carr_code in
 * the allowed-airline list) books via "Submit Indent" (booking_start only, no
 * payment step) — i.e. it is Non-Bookable. Flags, for every Rates_from
 * (FS / direct / offline):
 *   auto_search 0 = rate search enabled, 1 = skipped on a fresh search
 *   auto_book   0 = bookable (Proceed to Pay), 1 = non-bookable (Submit Indent)
 * So Submit Indent = auto_book 1 with auto_search 0.
 *
 * @param {Array<Object>} allowedAirlines - rows from GET allowed_airline_list/35
 * @param {Object} item - the selected rate option
 * @returns {boolean}
 */
const isAutoBookCarrier = (allowedAirlines, item) => {
  if (!Array.isArray(allowedAirlines) || !allowedAirlines.length) return false;
  const name = normName(item?.carrierName || item?.flightItineraries?.[0]?.carrierName);
  const code = normName(item?.carrierCode || item?.flightItineraries?.[0]?.carrierCode);
  if (!name && !code) return false;
  const entry = allowedAirlines.find(
    (a) => (name && normName(a?.carr_name) === name) || (code && normName(a?.carr_code) === code)
  );
  if (!entry) return false;
  return isFlagOn(entry.auto_book) && !isFlagOn(entry.auto_search);
};

/**
 * @param {Object} props
 * @param {Object} props.selectedItem - the rate/booking the user is confirming,
 *   including flightItineraries and rateDetails.chargesBreakDown used to
 *   build the Payment Summary and the GST breakdown
 * @param {Object} [props.surchargeSelection] - which optional surcharges the
 *   user picked on the search/results page (kept for backward compatibility;
 *   every charge in chargesBreakDown is now always included in the total)
 * @param {Array<Object>} [props.allowedAirlines] - allowed_airline_list/35 rows
 *   fetched alongside ratesearch/all on the results page; decides Submit
 *   Indent vs Proceed to Pay (empty → Proceed to Pay)
 * @returns {JSX.Element} the booking confirmation / payment page
 */
const NewBookingFlowContent = ({ selectedItem, surchargeSelection, allowedAirlines = [] }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isChecked, setIsChecked] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [spinner, loadSpinner] = useState(false);
  const [surchargesExpanded, setSurchargesExpanded] = useState(false);
  const [viewBreakupData, setviewBreakupData] = useState({});
  const [lattaoData, setLattoaData] = useState({});
  const [airportdata, setairportdata] = useState([]);
  // Whole `commodity-details/<commodityId>` record (see the lookup effect
  // below): { commodityDescription, QR_commodityCode, productCode,
  // commodityShc, commodityGroup, hscode, … }. Kept as the full object rather
  // than just the description because the header chips and the Goods
  // Description panel show the carrier codes from it too.
  const [commodityMaster, setCommodityMaster] = useState(null);
  const commodityDescription = commodityMaster?.commodityDescription || "";
  const [selectedValue, setSelectedValue] = useState("Paper AWB");

  // Payment state
  const [paymentSessionId, setPaymentSessionId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const cashfreeInitRef = useRef(false);

  // Booking success modal
  const [showBookingSuccess, setShowBookingSuccess] = useState(false);
  const [bookingSuccessData, setBookingSuccessData] = useState(null);
  const [countdown, setCountdown] = useState(12);

  // Payment pending modal (shown between CashFree success and booking API response)
  const [showPaymentPending, setShowPaymentPending] = useState(false);

  // Update pending modal (shown when no payment needed but booking update is in progress)
  const [showUpdatePending, setShowUpdatePending] = useState(false);

  const state = useSelector(updateStoredSearch);
  const { newsearchformdata, rqueryid, mannualsearchdata } =
    state?.payload?.newsearchform;
  const socketBookingId = useSelector(selectStoredBookingId);
  const socketQueryId = useSelector(selectStoredQueryId);
  const prevBookingAmount = useSelector(selectPrevBookingAmount);
  const storedAwbNumber = useSelector(selectStoredAwbNumber);
  // Origin airport list — only used for the `iataCass` fallback lookup inside
  // buildRateSearchPayload (the query itself usually already carries `cass`).
  const { originairports } = useSelector(searchratestate);
  const [fetchedAwbNumber, setFetchedAwbNumber] = useState(null);
  // Fallback bookingId resolved from single_booking_data when it isn't present on
  // the socket store or the selectedItem (e.g. resuming an update flow on reload).
  const [fetchedBookingId, setFetchedBookingId] = useState(null);
  const { searchquery } = newsearchformdata;

  // The booking-update payload must carry shipmentTotal.type (the weight-mode:
  // 1 = KG/UNIT, 2 = TOTAL). The stored search sometimes omits it even though
  // packagingType.weightMode is present, so mirror weightMode as the fallback
  // to match what the create flow sends.
  const updateSearchquery = (() => {
    if (!searchquery) return null;
    const { packagingDetails } = searchquery;
    const shipmentTotal = packagingDetails?.shipmentTotal;
    const resolvedType =
      shipmentTotal?.type ?? packagingDetails?.packagingType?.weightMode;
    if (resolvedType === undefined || resolvedType === null) return searchquery;
    return {
      ...searchquery,
      packagingDetails: {
        ...packagingDetails,
        shipmentTotal: {
          ...shipmentTotal,
          type: resolvedType,
        },
      },
    };
  })();

  // Manual-quote expiry. The value may or may not have been threaded through the
  // search payload (depends on how this booking flow was entered), so resolve it
  // authoritatively from the query document via the queryId available here, and
  // fall back to whatever the stored search carried.
  const [fetchedQuoteExpiry, setFetchedQuoteExpiry] = useState(null);
  const resolvedQueryId = rqueryid || socketQueryId || searchquery?._id;
  useEffect(() => {
    if (!resolvedQueryId) return;
    dispatch(get_single_search_query(resolvedQueryId))
      .then((res) => {
        const exp = res?.payload?.data?.nodeData?.result?.queryExpiryDate;
        if (exp) setFetchedQuoteExpiry(exp);
      })
      .catch(() => { });
  }, [resolvedQueryId]);

  // Once the expiry instant is at or before "now", the quote has lapsed and
  // payment must be blocked — disable the Proceed to Pay / Pay Difference button.
  const effectiveQuoteExpiry =
    fetchedQuoteExpiry ?? searchquery?.quoteExpiry ?? null;
  const isQuoteExpired =
    !!effectiveQuoteExpiry &&
    moment(effectiveQuoteExpiry).isSameOrBefore(moment());

  function extractAirportCode(str) {
    const match = str?.match(/\(([^)]+)\)/);
    return match ? match[1] : "";
  }

  const or = extractAirportCode(
    searchquery?.originAirport?.originAirport ||
      mannualsearchdata?.originPort?.originAirport
  );
  const des = extractAirportCode(
    searchquery?.destinationAirport?.destinationAirport ||
      mannualsearchdata?.destinationPort?.destinationAirport
  );

  const originCountry =
    (searchquery?.originAirport || mannualsearchdata?.originPort)?.origincountryName;
  const destCountry =
    (searchquery?.destinationAirport || mannualsearchdata?.destinationPort)?.destinationcountryName;

  const originFlag =
    (searchquery?.originAirport || mannualsearchdata?.originPort)?.originLogoUrl ||
    getFlagUrl(originCountry) ||
    "";
  const destinationFlag =
    (searchquery?.destinationAirport || mannualsearchdata?.destinationPort)?.destinationLogoUrl ||
    getFlagUrl(destCountry) ||
    "";

  const packagingPackages =
    newsearchformdata?.searchquery?.packagingDetails?.packagingType?.packages;

  const shipmentTotal =
    searchquery?.packagingDetails?.shipmentTotal ||
    mannualsearchdata?.packagingDetails?.shipmentTotal;

  const gdQty =
    shipmentTotal?.totalunits ||
    selectedItem?.flightItineraries?.[0]?.pcs;

  const gdVol =
    shipmentTotal?.totalvolume ||
    selectedItem?.flightItineraries?.[0]?.volume;

  const gdGW =
    shipmentTotal?.totalweight ||
    selectedItem?.flightItineraries?.[0]?.weight;

  const gdWunit = shipmentTotal?.weightunit || selectedItem?.rateDetails?.chargeableWeightUnit || "KG";
  const gdVolUnit = shipmentTotal?.unit || "CBM";

  // The saved query document this booking came from, as published to redux by
  // the Queries page / the quotation deep-link. Only trusted when it IS this
  // booking's query — it survives in redux across navigations, so a booking
  // started from an unrelated search must never inherit an older query's
  // commodity/temperature.
  const storedQueryDoc = state?.payload?.newqueries?.newqueriesdata;
  const storedQueryDocForThisBooking =
    storedQueryDoc?._id &&
      resolvedQueryId &&
      String(storedQueryDoc._id) === String(resolvedQueryId)
      ? storedQueryDoc
      : null;

  // ── Selected temperature range (Perishables only) ────────────────────────
  // The search form publishes the picked range as a bare string ("-10", "2-8",
  // "15-25", "2-25") both at the top level of the stored search and inside
  // searchquery; the booking-resume path carries it on airlinepayloadData.
  // Empty for every non-temperature-controlled commodity, so the row is hidden.
  // formatTemperatureRange renders it per bound ("2-8" -> "2°C to 8°C", "-10" ->
  // "-10°C") so this screen matches the booking-detail and quote headers.
  //
  // A "Quote Available" query booked straight from its saved quote never went
  // through the search form (Queries / the quotation deep-link hand the results
  // page `buildAirSearchPayload(queryDoc)`), so none of the sources above are
  // populated — the range lives on the query document, which those same callers
  // publish to redux via `newqueries`. Tried last, so every existing source
  // still wins unchanged.
  const gdTempRangeRaw =
    newsearchformdata?.temperatureRange ||
    searchquery?.temperatureRange ||
    selectedItem?.airlinepayloadData?.temperatureRange ||
    mannualsearchdata?.temperatureRange ||
    searchquery?.goodsDetails?.temperatureRange ||
    storedQueryDocForThisBooking?.temperatureRange ||
    "";
  const gdTempRange = formatTemperatureRange(gdTempRangeRaw);

  // ── Commodity for the header/summary rows ────────────────────────────────
  // `commodityDescription` (fetched from commodity-details below) stays the
  // primary source; these fallbacks only fire when the lookup had no
  // commodityId to run on or hasn't resolved yet.
  const gdCommodityDescription =
    commodityDescription ||
    newsearchformdata?.commodity_description ||
    searchquery?.goodsDetails?.commodity_description ||
    selectedItem?.goodsDetails?.commodity_description ||
    mannualsearchdata?.goodsDetails?.commodity_description ||
    storedQueryDocForThisBooking?.goodsDetails?.commodity_description ||
    // Last: the search form seeds this with a "General Cargo" default, so it
    // must never mask a real commodity carried by any source above.
    newsearchformdata?.productDetails?.natureOfGoods ||
    "";

  // ── Vol weight: prefer stored totalvolweight for exact value ─────────────
  // Falls back to formula-derived value for older records without the field.
  const _storedVW = shipmentTotal?.totalvolweight;
  // Guard: > 0 so a stored 0 (empty-form init) falls through to the fallback.
  const gdVW = _storedVW != null && parseFloat(_storedVW) > 0
    ? parseFloat(_storedVW)
    : gdVol
      ? gdVolUnit === "CFT"
        ? parseFloat(gdVol) * 0.0283168 * 166.67
        : parseFloat(gdVol) * 166.67
      : null;
  // ─────────────────────────────────────────────────────────────────────────
  const gdCWRaw = shipmentTotal?.chargeableweight;
  // ── IN mode: ignore API's chargeableWeight (was computed with CM dims) ───
  // When unit is CFT the API rate was quoted against CM-based vol weight, so
  // rateDetails.chargeableWeight is wrong for the IN case.
  // Recompute from max(GW, VW_in) so it matches the form footer value.
  const gdCW = gdVolUnit === "CFT"
    ? (gdGW != null && gdVW != null ? Math.max(Number(gdGW), gdVW) : null)
    : selectedItem?.rateDetails?.chargeableWeight ||
      gdCWRaw ||
      (gdGW != null && gdVW != null ? Math.max(Number(gdGW), gdVW) : null);
  // ─────────────────────────────────────────────────────────────────────────

  const volumeWeightDisplay = gdVW;

  function MinutesToHours(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours} hr ${mins} min`;
  }

  const getAirportNameByCode = (code) => {
    const airport = (airportdata || []).find((a) => a?.code === code);
    return airport ? airport.name : "Not Found";
  };

  const handleRadioChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const showAwbOptions = selectedItem?.flightItineraries?.[0]?.rates_from === "QR";
  // Set only for an EK rate; null for every other carrier.
  const ekRateQuotationID = getEkRateQuotationID(selectedItem);
  // EK fields every booking payload (booking_start, booking, bookingUpdate)
  // carries, so the airline books that exact product quotation.
  const ekBookingFields = ekRateQuotationID
    ? {
        rateQuotationID: ekRateQuotationID,
        ...(selectedItem?.productDescription && {
          productDescription: selectedItem.productDescription,
        }),
      }
    : null;

  // Offline (stored/manual) rates come back from /ratesearch/all carrying
  // `space_availability` on the selected availabilityResponseSO. The booking
  // endpoints need the same number so the backend books against the space the
  // user actually saw, so echo it on every booking payload. Online carrier
  // rates don't carry the key — then this is null and nothing is added
  // (spreading null is a no-op), so the booking stores no space and the
  // booking-detail pages leave the Space Availability cell out entirely.
  const spaceAvailabilityFields =
    selectedItem?.space_availability !== undefined &&
    selectedItem?.space_availability !== null
      ? { space_availability: selectedItem.space_availability }
      : null;

  // Enrich goodsDetails for the booking-update payload. selectedItem.goodsDetails
  // is missing commodityId / commodity_description / specialHandlingCodes, so merge
  // them in from the search query (and the commodity-details lookup) before sending.
  const enrichedGoodsDetails = {
    ...(selectedItem?.goodsDetails || {}),
    commodityId:
      selectedItem?.goodsDetails?.commodityId ??
      newsearchformdata?.searchquery?.goodsDetails?.commodityId ??
      searchquery?.commodityId,
    commodity_description:
      commodityDescription ||
      selectedItem?.goodsDetails?.commodity_description ||
      newsearchformdata?.searchquery?.goodsDetails?.cargoType ||
      "General Cargo",
    specialHandlingCodes:
      selectedItem?.goodsDetails?.specialHandlingCodes ??
      newsearchformdata?.searchquery?.goodsDetails?.specialHandlingCodes ??
      [],
  };

  // productCommodityDetails as the rate socket returned it for the selected
  // option (productCode / commodityCode / natureOfGoods / densityGroup /
  // dgShipment / serviceCode / specialHandlingCodes). This is the authoritative
  // SHC set for the booking — the airline's own codes for the product the user
  // picked (e.g. ["COL","VIC","PER"] for "QR Fresh Passive") — so it is sent
  // through to booking_start and on to the booking payload, and the backend
  // uses these codes rather than the ones from the original search query.
  // The search-query codes are kept only as a fallback for older/manual rates
  // whose option object carries no productCommodityDetails at all.
  const resolvedProductCommodityDetails = (() => {
    const fromRate =
      selectedItem?.productCommodityDetails ||
      selectedItem?.airlinepayloadData?.productDetails ||
      null;
    const fallbackShc =
      newsearchformdata?.productDetails?.specialHandlingCodes ||
      newsearchformdata?.searchquery?.goodsDetails?.specialHandlingCodes ||
      searchquery?.goodsDetails?.specialHandlingCodes ||
      [];
    if (!fromRate) {
      return fallbackShc.length ? { specialHandlingCodes: fallbackShc } : null;
    }
    // EK: send the product's block exactly as the airline returned it —
    // specialHandlingCodes included, even [""] — never fill it from the search.
    if (ekRateQuotationID) return fromRate;
    // Never overwrite the rate's own codes — only fill in when absent/empty.
    const rateShc = fromRate?.specialHandlingCodes;
    if (Array.isArray(rateShc) && rateShc.length > 0) return fromRate;
    return { ...fromRate, specialHandlingCodes: fallbackShc };
  })();

  // ── Carrier commodity codes for the header chips / Goods Description ──────
  // The commodity master (commodity-details/<commodityId>) is the primary
  // source: it's the row the user actually picked in the search form, and it
  // carries the airline's codes for it (QR_commodityCode "9978", productCode
  // "QFR", commodityShc "PER"). The rate option's own productCommodityDetails
  // is kept as the fallback for bookings whose commodityId didn't resolve
  // (manual quotes, older stored searches) so nothing regresses there.
  // Each value is optional — a missing one simply drops its chip/row instead
  // of rendering an empty label.
  const gdCommodityCode =
    commodityMaster?.QR_commodityCode ||
    resolvedProductCommodityDetails?.commodityCode ||
    "";
  const gdProductCode =
    commodityMaster?.productCode ||
    resolvedProductCommodityDetails?.productCode ||
    "";
  // commodityShc arrives as a single code or a comma-separated string ("PER",
  // "PER,COL"); the rate option carries an array instead.
  const gdShcList = (() => {
    const raw = commodityMaster?.commodityShc;
    const fromMaster = String(raw ?? "")
      .split(",")
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    if (fromMaster.length) return [...new Set(fromMaster)];
    const fromRate = resolvedProductCommodityDetails?.specialHandlingCodes;
    return Array.isArray(fromRate)
      ? [...new Set(fromRate.map((c) => String(c).trim().toUpperCase()).filter(Boolean))]
      : [];
  })();
  const gdShc = gdShcList.join(", ");
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Last two are the "Quote Available → booked straight from the saved quote"
    // path, where the id only exists on the stored search root / query document.
    const commodityId = newsearchformdata?.searchquery?.goodsDetails?.commodityId||searchquery?.commodityId||newsearchformdata?.commodityId||storedQueryDocForThisBooking?.goodsDetails?.commodityId;
    if (commodityId) {
      httpinstance
        .get(`commodity-details/${commodityId}`)
        .then((res) => {
          // { id, hscode, commodityDescription, CommodityDescription,
          //   commodityGroup, commodityShc, QR_commodityCode, productCode }
          const record = res?.data?.data?.data;
          if (record) setCommodityMaster(record);
        })
        .catch(() => {});
    }
  }, [newsearchformdata,searchquery,storedQueryDocForThisBooking]);

  useEffect(() => {
    dispatch(airport_list())
      .then((res) => {
        const list = res?.payload?.data?.data;
        setairportdata(Array.isArray(list) ? list : []);
      })
      .catch((err) => console.error(err));

    const params = {
      origin: or,
      destination: des,
      incoTerms: searchquery?.paymentTerms?.incoTerms,
      optionalVas:
        searchquery?.paymentTerms?.optionalVas?.map((item) => item.vatId) || [],
      totalWeight: searchquery?.packagingDetails?.shipmentTotal?.totalweight,
      freightCharges: selectedItem?.rateDetails?.freightCharges,
    };
    if (searchquery?.originAirport?.originPickup) {
      params.pickupCharges = selectedItem?.rateDetails?.pickupCharges;
    }
    if (searchquery?.destinationAirport?.destinationPickup) {
      params.deliveryCharges = selectedItem?.rateDetails?.deliveryCharges;
    }
    if (rqueryid !== undefined) {
      params.queryid = rqueryid;
    }
    dispatch(viewBreakUp(params))
      .then((res) => setviewBreakupData(res?.payload?.data?.data))
      .catch(() => {});

    const lattaopayload = {
      origin: or,
      destination: des,
      operation_type: selectedItem?.flightItineraries?.[0]?.operationType,
      special_handling_code:
        newsearchformdata?.specialHandlingCodes ||
        newsearchformdata?.searchquery?.goodsDetails?.specialHandlingCodes,
    };
    dispatch(lattao(lattaopayload))
      .then((res) => setLattoaData(res?.payload?.data))
      .catch((err) => console.error(err));
  }, []);

  // Fetch airWaybillNumber from single_booking_data for update flow
  useEffect(() => {
    const queryId = socketQueryId ?? rqueryid;
    if (newsearchformdata?.requestType !== "2" || !queryId) return;
    dispatch(getsinglebookingdata({ queryId }))
      .then((res) => {
        const awb = res?.payload?.data?.data?.airWaybillNumber;
        if (awb) setFetchedAwbNumber(awb);
      })
      .catch(() => {});
  }, []);

  // Fetch bookingId from single_booking_data when it's missing on the selected item
  useEffect(() => {
    const resolvedBookingId = socketBookingId ?? selectedItem?.bookingId;
    if (resolvedBookingId !== null && resolvedBookingId !== undefined) return;
    const queryId = socketQueryId ?? rqueryid;
    if (!queryId) return;
    dispatch(getsinglebookingdata({ queryId }))
      .then((res) => {
        console.log("getsinglebookingdata response:", res);
        console.log("getsinglebookingdata data:", res?.payload?.data?.data);
        const bookingId = res?.payload?.data?.data?.bookeditem?.bookingId;
        if (bookingId !== null && bookingId !== undefined) setFetchedBookingId(bookingId);
      })
      .catch((err) => console.error("getsinglebookingdata error:", err));
  }, [socketBookingId, selectedItem?.bookingId, socketQueryId, rqueryid]);

  // Initialise CashFree drop-in after modal opens and sessionId is set
  useEffect(() => {
    if (!paymentSessionId || !showPaymentModal || cashfreeInitRef.current)
      return;
    cashfreeInitRef.current = true;

    const container = document.getElementById("new_booking_drop_in");
    if (!container) return;

    const cashfree = new cashfreeSandbox.Cashfree(paymentSessionId);
    cashfree.drop(container, {
      onSuccess: (res) => {
        setShowPaymentModal(false);
        setShowPaymentPending(true);
        const verifyPayload = {
          orderid: orderId,
          requestType: isUpdateFlow ? "2" : "1",
          ...(isUpdateFlow && { queryId: socketQueryId ?? rqueryid }),
        };
        dispatch(payment_verification(verifyPayload))
          .then((verifyRes) => {
            const httpStatus = verifyRes?.payload?.status;
            const backendStatus = verifyRes?.payload?.data?.statusCode;
            if (httpStatus === 200 || backendStatus === 200) {
              if (isUpdateFlow) {
                console.log({
                  queryId: socketQueryId ?? rqueryid,
                  bookingData: { ...selectedItem, bookingId: socketBookingId ?? selectedItem?.bookingId ?? fetchedBookingId, goodsDetails: enrichedGoodsDetails },
                  searchquery: updateSearchquery,
                  airWayBillNumber: fetchedAwbNumber ?? storedAwbNumber,
                  requestType: "2",
                },"handle the update case")
                dispatch(bookingupdate({
                  // Rate-search keys go first so every key below still wins.
                  ...rateSearchPayload,
                  queryId: socketQueryId ?? rqueryid,
                  // Resolve bookingId from socket store, then selectedItem, then the fetched fallback
                  bookingData: {
                    ...selectedItem,
                    bookingId: socketBookingId ?? selectedItem?.bookingId ?? fetchedBookingId,
                    rateDetails: updatedRateDetails,
                    goodsDetails: enrichedGoodsDetails,
                    // Keep the carrier's productCommodityDetails/SHC on the update too
                    ...(resolvedProductCommodityDetails && {
                      productCommodityDetails: resolvedProductCommodityDetails,
                    }),
                    ...ekBookingFields,
                    ...spaceAvailabilityFields,
                  },
                  searchquery: updateSearchquery,
                  airWayBillNumber: fetchedAwbNumber ?? storedAwbNumber,
                  requestType: "2",
                  // requestType "2" confirms the booking through /bookingUpdate
                  // instead of /booking, so the offline rate's space has to go
                  // out here too — same root-level key /booking carries.
                  ...spaceAvailabilityFields,
                  ...((selectedValue === "EAP" || selectedValue === "EAW") && {
                    specialHandlingCode: selectedValue,
                  }),
                }))
                  .then((bookRes) => {
                    loadSpinner(false);
                    const bookStatus = bookRes?.payload?.data?.statusCode || bookRes?.payload?.statusCode;
                    if (bookStatus === 200 || bookStatus === 201) {
                      dispatch(clearStoredSearch());
                      dispatch(clearPrevBookingAmountStore());
                      dispatch(updatenotificationdata(bookRes?.payload?.data));
                      dispatch(clearstate());
                      dispatch(clearBookingIdStore());
                      dispatch(clearQueryIdStore());
                      dispatch(updatebookinghistorytabvalue(1));
                      setShowPaymentPending(false);
                      // Surface backend success message to the user via toast
                      toast.success(
                        bookRes?.payload?.data?.message || bookRes?.payload?.message || "Booking updated successfully",
                        { position: "top-center", autoClose: 3000, hideProgressBar: true }
                      );
                      setBookingSuccessData(bookRes?.payload?.data);
                      setCountdown(12);
                      setShowBookingSuccess(true);
                    } else {
                      toast.error(
                        bookRes?.payload?.data?.message || bookRes?.payload?.message || "Update failed!",
                        { position: "top-center", autoClose: 3000, hideProgressBar: true }
                      );
                      setShowPaymentPending(false);
                    }
                  })
                  .catch(() => {
                    toast.error("Update failed. Please contact support.", {
                      position: "top-center",
                      autoClose: 3000,
                    });
                    setShowPaymentPending(false);
                  });
              } else {
                const bookingPayload = {
                  // Rate-search keys go first so the booking's own keys below
                  // always win on any overlap.
                  ...rateSearchPayload,
                  queryId: rqueryid,
                  requestType: "1",
                  // Same block that went to booking_start, so the created
                  // booking stores the carrier's own specialHandlingCodes.
                  ...(resolvedProductCommodityDetails && {
                    productCommodityDetails: resolvedProductCommodityDetails,
                  }),
                  ...ekBookingFields,
                  // Offline rates: the space the user booked against, straight
                  // from the rate-search response.
                  ...spaceAvailabilityFields,
                  ...((selectedValue === "EAP" || selectedValue === "EAW") && {
                    specialHandlingCode: selectedValue,
                  }),
                };
                dispatch(booking(bookingPayload))
                  .then((bookRes) => {
                    loadSpinner(false);
                    const bookStatus =
                      bookRes?.payload?.data?.statusCode ||
                      bookRes?.payload?.statusCode;
                    if (bookStatus === 200 || bookStatus === 201) {
                      dispatch(clearStoredSearch());
                      dispatch(updatenotificationdata(bookRes?.payload?.data));
                      dispatch(clearstate());
                      dispatch(clearBookingIdStore());
                      dispatch(clearQueryIdStore());
                      dispatch(updatebookinghistorytabvalue(1));
                      setShowPaymentPending(false);
                      setBookingSuccessData(bookRes?.payload?.data);
                      setCountdown(12);
                      setShowBookingSuccess(true);
                    } else {
                      toast.error(
                        bookRes?.payload?.data?.message ||
                          bookRes?.payload?.message ||
                          "Booking failed!",
                        {
                          position: "top-center",
                          autoClose: 3000,
                          hideProgressBar: true,
                        }
                      );
                      setShowPaymentPending(false);
                    }
                  })
                  .catch(() => {
                    toast.error("Booking failed. Please contact support.", {
                      position: "top-center",
                      autoClose: 3000,
                    });
                    setShowPaymentPending(false);
                  });
              }
            } else {
              toast.error("Payment verification failed. Please try again.", {
                position: "top-center",
                autoClose: 3000,
              });
              setShowPaymentPending(false);
            }
          })
          .catch(() => {
            toast.error("Payment verification failed. Please try again.", {
              position: "top-center",
              autoClose: 3000,
            });
            setShowPaymentPending(false);
          });
      },
      onFailure: () => {
        toast.error("Payment failed. Please try again.", {
          position: "top-center",
          autoClose: 3000,
        });
        setShowPaymentModal(false);
        setPaymentSessionId("");
        cashfreeInitRef.current = false;
      },
      components: ["order-details", "card", "upi", "app", "netbanking"],
      style: {
        theme: "#FCB614",
        color: "#FCB614",
        fontSize: "14",
        backgroundColor: "#ffffff",
      },
    });
  }, [paymentSessionId, showPaymentModal]);

  useEffect(() => {
    if (!showBookingSuccess) return;
    if (countdown === 0) {
      navigate("/booking-history");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [showBookingSuccess, countdown]);

  const handleConfirmUpdate = () => {
    if (!isChecked) return;
    setShowUpdatePending(true);
    dispatch(remaining_balance_update({
      queryId: socketQueryId ?? rqueryid,
      remaining_balance: prevBookingAmount - totalAmount,
      currentBookingAmount: totalAmount,
    }));
    // For airlinePrefix 157, the backend expects date fields as "DD-MMM-YYYY"
    // and time fields as ISO-like "YYYY-MM-DDTHH:mm".
    const normalizedFlightItineraries = selectedItem?.flightItineraries?.map(
      (itinerary) => {
        if (Number(itinerary?.airlinePrefix) !== 157) return itinerary;
        const formatDate = (value) =>
          value
            ? moment(value, ["DD-MMM-YYYY", "DD-MMM-YYYY HH:mm", moment.ISO_8601]).format(
                "DD-MMM-YYYY",
              )
            : value;
        const formatTime = (value) =>
          value
            ? moment(value, ["DD-MMM-YYYY HH:mm", "DD-MMM-YYYY", moment.ISO_8601]).format(
                "YYYY-MM-DDTHH:mm",
              )
            : value;
        return {
          ...itinerary,
          scheduledArrivalDate: formatDate(itinerary?.scheduledArrivalDate),
          scheduledDepartureDate: formatDate(itinerary?.scheduledDepartureDate),
          scheduledArrivalTime: formatTime(itinerary?.scheduledArrivalTime),
          scheduledDepartureTime: formatTime(itinerary?.scheduledDepartureTime),
        };
      },
    );
    dispatch(bookingupdate({
      // Rate-search keys go first so every key below still wins.
      ...rateSearchPayload,
      queryId: socketQueryId ?? rqueryid,
      // bookingData: selectedItem,
                        bookingData: {
                          ...selectedItem,
                          flightItineraries: normalizedFlightItineraries ?? selectedItem?.flightItineraries,
                          // Resolve bookingId from socket store, then selectedItem, then the fetched fallback
                          bookingId: socketBookingId ?? selectedItem?.bookingId ?? fetchedBookingId,
                          rateDetails: updatedRateDetails,
                          goodsDetails: enrichedGoodsDetails,
                          // Keep the carrier's productCommodityDetails/SHC on the update too
                          ...(resolvedProductCommodityDetails && {
                            productCommodityDetails: resolvedProductCommodityDetails,
                          }),
                          ...ekBookingFields,
                          ...spaceAvailabilityFields,
                        },

      searchquery: updateSearchquery,
      airWayBillNumber: fetchedAwbNumber ?? storedAwbNumber,
      requestType: "2",
      // Same as the paid update path above — this call is what confirms the
      // booking, so it carries the offline rate's space at the root too.
      ...spaceAvailabilityFields,
      ...((selectedValue === "EAP" || selectedValue === "EAW") && {
        specialHandlingCode: selectedValue,
      }),
    }))
      .then((res) => {
        const status = res?.payload?.data?.statusCode || res?.payload?.statusCode;
        if (status === 200 || status === 201) {
          dispatch(clearStoredSearch());
          dispatch(clearPrevBookingAmountStore());
          dispatch(clearBookingIdStore());
          dispatch(clearQueryIdStore());
          dispatch(clearstate());
          dispatch(updatenotificationdata(res?.payload?.data));
          dispatch(updatebookinghistorytabvalue(1));
          setShowUpdatePending(false);
          // Surface backend success message to the user via toast
          toast.success(
            res?.payload?.data?.message || res?.payload?.message || "Booking updated successfully",
            { position: "top-center", autoClose: 3000, hideProgressBar: true }
          );
          setBookingSuccessData(res?.payload?.data);
          setCountdown(12);
          setShowBookingSuccess(true);
        } else {
          setShowUpdatePending(false);
          toast.error(res?.payload?.data?.message || res?.payload?.message || "Update failed!", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
          });
        }
      })
      .catch(() => {
        setShowUpdatePending(false);
        toast.error("Update failed. Please contact support.", {
          position: "top-center",
          autoClose: 3000,
        });
      });
  };

  // bookingData sent to booking_start — shared by Proceed to Pay and Submit Indent.
  const buildBookingStartData = () => ({
        productDescription: selectedItem?.productDescription || "General Cargo",
        speedName: selectedItem?.speedName || "Standard",
        carrierName: selectedItem?.carrierName,
        carrierLogo: selectedItem?.carrierLogo,
        carrierCode: selectedItem?.carrierCode ?? selectedItem?.flightItineraries?.[0]?.carrierCode ?? null,
        requestRefId: selectedItem?.requestRefId,
        origin: selectedItem?.origin,
        destination: selectedItem?.destination,
        flightItineraries: selectedItem?.flightItineraries,
        rateDetails: updatedRateDetails,
        // Carrier product + commodity block (incl. specialHandlingCodes) exactly
        // as the rate socket sent it — booking_start persists it so every
        // downstream step reads the SHC from here.
        productCommodityDetails: resolvedProductCommodityDetails,
        travelTime: selectedItem?.travelTime,
        hash: selectedItem?.hash,
        rateStatus: selectedItem?.rateStatus,
        LAT: selectedItem?.LAT ?? selectedItem?.lat ?? lattaoData?.lat ?? null,
        TOA: selectedItem?.TOA ?? selectedItem?.tao ?? lattaoData?.tao ?? null,
        // Resolve bookingId from socket store, then selectedItem, then the fetched fallback
        bookingId: socketBookingId ?? selectedItem?.bookingId ?? fetchedBookingId,
        // Fall back to rqueryid (same source as the top-level queryId) so
        // bookingData.queryId is always present — the backend requires it and
        // socketQueryId / selectedItem.queryId can both be absent (e.g. the
        // manual-quote flow).
        queryId: socketQueryId ?? selectedItem?.queryId ?? rqueryid,
        ratesFrom: selectedItem?.ratesFrom ?? selectedItem?.flightItineraries?.[0]?.rates_from,
        ...ekBookingFields,
        ...spaceAvailabilityFields,
  });

  /**
   * handleSubmitIndent
   * ------------------
   * Booking path for auto-book carriers (see isAutoBookCarrier): calls
   * booking_start only — no order / payment step — and on any 2xx redirects to
   * booking history. Non-2xx keeps the user here with the API's message.
   *
   * API: POST booking_start { queryId, bookingAmount, requestType, bookingData }
   */
  const handleSubmitIndent = () => {
    if (!isChecked || spinner) return;
    const payload = {
      queryId: socketQueryId ?? rqueryid,
      bookingAmount: Number(amountToPay),
      requestType: isUpdateFlow ? "2" : "1",
      bookingData: buildBookingStartData(),
    };
    console.log("booking_start (Submit Indent) payload:", payload);

    loadSpinner(true);
    dispatch(bookingStart(payload))
      .then((res) => {
        loadSpinner(false);
        // Thunk returns the axios response (or err.response) — check the HTTP
        // status first, then the body's statusCode.
        const status = Number(res?.payload?.status ?? res?.payload?.data?.statusCode);
        if (status >= 200 && status < 300) {
          toast.success(res?.payload?.data?.message || "Indent submitted successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
          });
          navigate("/booking-history");
          return;
        }
        toast.error(
          res?.payload?.data?.message || res?.payload?.message || "Failed to submit indent!",
          { position: "top-center", autoClose: 3000, hideProgressBar: true }
        );
      })
      .catch(() => {
        loadSpinner(false);
        toast.error("Failed to submit indent. Please try again.", {
          position: "top-center",
          autoClose: 3000,
        });
      });
  };

  const handleProceedToPay = () => {

    if (!isChecked) return;
  console.log(socketBookingId,selectedItem,"selectedItem?.bookingId")

    const paymentVerifyPayload = {
      bookingData: buildBookingStartData(),
      queryId: socketQueryId ?? rqueryid,
      // Use the breakdown-derived total (sum of every charge), not the backend's
      // original rateDetails.totalAmount which predates the all-charges breakdown.
      bookingAmount: Number(totalAmount),
      rates_from: selectedItem?.flightItineraries?.[0]?.rates_from,
    };
    console.log("payment_verify_status payload:", paymentVerifyPayload);

    loadSpinner(true);
    dispatch(bookingStart({
      queryId: socketQueryId ?? rqueryid,
      bookingAmount: Number(amountToPay),
      requestType: isUpdateFlow ? "2" : "1",
      bookingData: paymentVerifyPayload.bookingData,
    }))
      .then((bookingStartRes) => {
        const bookingStartStatus =
          bookingStartRes?.payload?.data?.statusCode ||
          bookingStartRes?.payload?.statusCode;
        if (bookingStartStatus !== 200 && bookingStartStatus !== 201) {
          loadSpinner(false);
          toast.error(
            bookingStartRes?.payload?.data?.message ||
              bookingStartRes?.payload?.message ||
              "Failed to start booking!",
            { position: "top-center", autoClose: 3000, hideProgressBar: true }
          );
          return;
        }
        dispatch(
          getorderid({
            queryId: rqueryid,
            bookingAmount: Number(amountToPay),
            requestType: isUpdateFlow ? "2" : "1",
            ...(isUpdateFlow && {
              bookingData: paymentVerifyPayload.bookingData,
              searchquery: updateSearchquery,
            }),
          })
        )
          .then((res) => {
            loadSpinner(false);
            const resData = res?.payload?.data;
            if (resData?.statusCode === 200) {
              setPaymentSessionId(resData?.data?.payment_session_id);
              setOrderId(resData?.data?.orderId);
              cashfreeInitRef.current = false;
              setShowPaymentModal(true);
            } else {
              toast.error(resData?.message || "Failed to create order!", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: true,
              });
            }
          })
          .catch(() => {
            loadSpinner(false);
            toast.error("Failed to create order. Please try again.", {
              position: "top-center",
              autoClose: 3000,
            });
          });
      })
      .catch(() => {
        loadSpinner(false);
        toast.error("Failed to start booking. Please try again.", {
          position: "top-center",
          autoClose: 3000,
        });
      });
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentSessionId("");
    cashfreeInitRef.current = false;
  };

  // Strip any previously-persisted GST line before recomputing GST below.
  // Why: an update-flow reopen (requestType "2") loads the *already-booked*
  // rateDetails.chargesBreakDown, which — for any booking made after GST was
  // introduced — already contains a "GST" category charge from the original
  // booking. If we didn't filter it out here, the Taxable Amount used to
  // recompute GST would itself already include the old GST amount, so the
  // new GST Amount would be calculated on (charges + GST) instead of just
  // (charges), silently double-taxing the customer on every update.
  const chargesBreakDown = (selectedItem?.rateDetails?.chargesBreakDown || []).filter(
    (c) => c.category !== "GST"
  );
  const itemRatesFrom = selectedItem?.flightItineraries?.[0]?.rates_from ?? selectedItem?.ratesFrom;
  // The carrier code often comes as a separate field while carrierNumber is just
  // the bare digits (e.g. "0579"). Prepend the code for every rate source, but
  // only when the number doesn't already begin with it (compared case-insensitively),
  // so "QR" + "QR0579" stays "QR0579" instead of "QR QR0579".
  const formatFlightNo = (carrierCode, carrierNumber) => {
    if (!carrierCode) return carrierNumber;
    const code = String(carrierCode).toLowerCase();
    const number = String(carrierNumber ?? "").toLowerCase();
    return number.startsWith(code)
      ? carrierNumber
      : `${carrierCode} ${carrierNumber}`;
  };

  // LAT/TOA can arrive either as a bare number (e.g. 300) or as a pre-formatted
  // string ("300 Mins"). Number("300 Mins") is NaN, so the old `Number(x) > 0`
  // guard treated the pre-formatted value as invalid and showed "N/A". Use
  // parseFloat for the numeric check (parseFloat("300 Mins") === 300) and, when
  // the raw value already carries a unit/text, display it verbatim; otherwise
  // append " Mins" (except for QR, whose value is already unit-formatted upstream).
  const formatLatToa = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "N/A";
    const num = parseFloat(String(raw));
    if (!(num > 0)) return "N/A";
    const hasUnitText = /[a-zA-Z]/.test(String(raw));
    if (hasUnitText) return String(raw);
    return `${raw}${itemRatesFrom === "QR" ? "" : " Mins"}`;
  };
  // EK rates carry a full chargesBreakDown too, so they get the same grouped
  // Payment Summary / Taxable Amount / GST-line treatment as FS.
  const hasBreakdown =
    (itemRatesFrom === 'FS' || itemRatesFrom === 'manualQoute' || itemRatesFrom === 'EK') &&
    chargesBreakDown.length > 0;
  const getAmt = (c) => Number(c?.Totalamount ?? c?.totalAmount ?? c?.chargeAmount ?? 0);
  // Charge label — field name differs per rate source.
  const chargeLabel = (c) =>
    c?.name || c?.chargeName || c?.chargeDescription || c?.chargeType || c?.category || "Charge";
  const fmtAmt = (val) => `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtKg = (val) => `₹${Number(val ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kg`;

  // ── all-charges change ───────────────────────────────────────────────────
  // The booking/payment page shows AND charges for every line in the breakdown.
  // Charges are grouped by `category` only for display (so each group renders
  // under its own header); the grouping no longer decides what is included in
  // the total — every charge is summed regardless of the listing-page toggles.
  // ─────────────────────────────────────────────────────────────────────────

  // Group charges by category for the grouped display blocks.
  const mainCharges = chargesBreakDown.filter(c => c.category === 'Main');           // Air Freight
  const fuelSecCharges = chargesBreakDown.filter(c => c.category === 'Fuel' || c.category === 'Security'); // Fuel & Security
  const otherCharges = chargesBreakDown.filter(c => c.category === 'Other taxes');   // Other Fees
  const screeningCharges = chargesBreakDown.filter(c => c.category === 'Screening'); // Screening
  // Catch-all: any charge whose category isn't one of the known groups above is
  // still rendered (under "Other Charges") and summed — nothing is dropped.
  const knownCategories = ['Main', 'Fuel', 'Security', 'Other taxes', 'Screening'];
  const miscCharges = chargesBreakDown.filter(c => !knownCategories.includes(c.category));

  // Per-group subtotals — used only for each group's header amount.
  const mainTotal = mainCharges.reduce((s, c) => s + getAmt(c), 0);
  const fuelSecTotal = fuelSecCharges.reduce((s, c) => s + getAmt(c), 0);
  const otherTotal = otherCharges.reduce((s, c) => s + getAmt(c), 0);
  const screeningTotal = screeningCharges.reduce((s, c) => s + getAmt(c), 0);
  const miscTotal = miscCharges.reduce((s, c) => s + getAmt(c), 0);

  // Render a group whenever it has charges. No surchargeSelection gating anymore —
  // all charges are always shown and always included in the total.
  const showOtherFees = hasBreakdown && otherCharges.length > 0;
  const showScreening = hasBreakdown && screeningCharges.length > 0;
  const showMisc = hasBreakdown && miscCharges.length > 0;

  // Taxable Amount = sum of EVERY charge in the breakdown (not group-by-group,
  // so any uncategorised charge is included too), excluding GST (already
  // filtered out above). Falls back to the backend total only when there is
  // no breakdown to sum (non-FS rates).
  const subtotalAmount = hasBreakdown
    ? chargesBreakDown.reduce((s, c) => s + getAmt(c), 0)
    : Number(selectedItem?.rateDetails?.totalAmount ?? 0);

  // GST Amount = Taxable Amount × configured GST rate (currently a fixed 18%,
  // not yet an admin-configurable setting). Net Amount = Taxable + GST — this
  // is the figure actually charged to the customer and persisted as the
  // booking's rateDetails.totalAmount (see updatedRateDetails below), so every
  // downstream read (Booking Details, invoice) reconstructs the same figures.
  const GST_RATE = 0.18;
  const gstAmount = subtotalAmount * GST_RATE;
  const totalAmount = subtotalAmount + gstAmount;

  const requestType = newsearchformdata?.requestType;
  const isUpdateFlow = requestType === "2";
  const differenceAmount = isUpdateFlow ? Math.max(0, totalAmount - prevBookingAmount) : 0;
  const noPaymentNeeded = isUpdateFlow && prevBookingAmount >= totalAmount;
  const payDifference = isUpdateFlow && prevBookingAmount < totalAmount;
  const amountToPay = payDifference ? differenceAmount : totalAmount;
  // A MANUAL-QUOTE rate quoted as "Subject to Availability"
  // (space_availability 2) has no confirmed space behind it yet, so nothing
  // can be charged for it up front: the user submits an indent instead and
  // the query stays at Confirmation Awaited until the space is confirmed.
  // Scoped to manual quotes only — online/stored rates are untouched, as is
  // "Space Available" (1) and any rate carrying no space at all.
  const isManualQuoteRate = normName(itemRatesFrom) === "manualquote";
  const isSubjectToAvailability =
    isManualQuoteRate && Number(selectedItem?.space_availability) === 2;
  // New bookings on auto-book carriers go through "Submit Indent" instead of
  // the payment step. Update flows (requestType "2") keep their own buttons.
  const isSubmitIndent =
    !isUpdateFlow &&
    (isSubjectToAvailability || isAutoBookCarrier(allowedAirlines, selectedItem));

  // Effective per-kg rate = sum of every charge's ratePerkilo across the whole
  // breakdown, so the displayed/saved rate matches the all-charges total above.
  const updatedRatePerkilo = hasBreakdown
    ? chargesBreakDown.reduce((s, c) => s + Number(c?.ratePerkilo ?? 0), 0)
    : Number(selectedItem?.rateDetails?.ratePerkilo ?? 0);

  // Pass every charge through to the bookingStart/booking payload unchanged.
  // GST is NOT appended as a chargesBreakDown line — it lives only in the
  // plain rateDetails fields below (taxableAmount/gstAmount/gstPercentage).
  const selectedChargesBreakDown = chargesBreakDown;

  // rateDetails sent in the booking payload — overridden with the breakdown-derived
  // values so the backend persists the same all-charges total the user paid.
  // taxableAmount/gstAmount are persisted as plain rateDetails fields — not as
  // an entry in chargesBreakDown. Downstream read-only views (Booking Details,
  // booking-view, the invoice) read these fields whenever chargesBreakDown has
  // no "GST" category.
  const updatedRateDetails = {
    ...(selectedItem?.rateDetails ?? {}),
    ratePerkilo: updatedRatePerkilo,   // all-charges per-kg
    totalAmount: totalAmount,          // all-charges grand total (Net Amount)
    chargesBreakDown: selectedChargesBreakDown, // full breakdown, nothing dropped
    taxableAmount: subtotalAmount,
    gstAmount: gstAmount,
    gstPercentage: GST_RATE * 100,     // the rate GST was calculated at (e.g. 18), sent alongside the amount so the backend/invoice never has to hardcode it separately
    // Only when the rate carries them (e.g. EK: INR / AED).
    ...(selectedItem?.rateDetails?.origin_currency && {
      origin_currency: selectedItem.rateDetails.origin_currency,
    }),
    ...(selectedItem?.rateDetails?.destination_currency && {
      destination_currency: selectedItem.rateDetails.destination_currency,
    }),
  };


  // The same keys the air rate search sends to /ratesearch/all, rebuilt flat so
  // they can be spread at the root of the booking payloads below. The builder
  // deliberately omits requestType / queryId / bookingId / airwaybillNumber /
  // booking_status, so every existing key on those payloads keeps winning —
  // see src/utils/buildRateSearchPayload.js. Returns {} for an ocean search.
  const rateSearchPayload = useMemo(
    () =>
      buildRateSearchPayload(newsearchformdata, {
        originairports,
        requestType,
      }),
    [newsearchformdata, originairports, requestType],
  );

  return (
    <>
      {spinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-10 z-50">
          <Spin />
        </div>
      )}

      <div className="w-full">
        <div className="p-6 flex flex-col gap-4">

          {/* Flight Summary Banner */}
          {selectedItem?.flightItineraries?.length > 0 && (
            <div className="bg-[#FFF9E6] rounded-xl border border-[#F5DFA0] px-5 pt-4 pb-3">
              <div className="flex items-center gap-4">

                {/* Airline logo — always visible, fallback plane icon */}
                <div className="w-16 h-16 rounded-xl border border-[#e8cc7a] bg-white flex items-center justify-center p-2 shrink-0 shadow-sm">
                  {selectedItem?.carrierLogo ? (
                    <img
                      src={selectedItem.carrierLogo}
                      alt="airline"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-[#f1b21b]">
                      <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                    </svg>
                  )}
                </div>

                {/* Origin */}
                <div className="flex items-center gap-2.5 shrink-0">
                  {originFlag && (
                    <img
                      src={originFlag}
                      alt="origin flag"
                      className="w-10 h-7 object-cover rounded shadow-sm shrink-0"
                    />
                  )}
                  <div>
                    <p className="text-2xl font-bold text-gray-800 leading-tight">
                      {selectedItem?.flightItineraries?.[0]?.segmentOfDeparture}
                    </p>
                    <p className="text-xs text-gray-500">
                      {moment(selectedItem?.flightItineraries?.[0]?.scheduledDepartureTime).format("DD MMM")}
                    </p>
                  </div>
                </div>

                {/* Connecting arrow */}
                <div className="flex-1 flex items-center">
                  <div className="flex-1 border-t border-[#e8cc7a]"></div>
                  <div className="w-9 h-9 rounded-full bg-[#f1b21b] flex items-center justify-center mx-2 shadow shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white rotate-90">
                      <path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2 1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" />
                    </svg>
                  </div>
                  <div className="flex-1 border-t border-[#e8cc7a]"></div>
                </div>

                {/* Destination */}
                <div className="flex items-center gap-2.5 shrink-0">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-800 leading-tight">
                      {selectedItem?.flightItineraries?.[selectedItem?.flightItineraries?.length - 1]?.segmentOfArrival}
                    </p>
                    <p className="text-xs text-gray-500">
                      {moment(selectedItem?.flightItineraries?.[selectedItem?.flightItineraries?.length - 1]?.scheduledArrivalTime).format("DD MMM")}
                    </p>
                  </div>
                  {destinationFlag && (
                    <img
                      src={destinationFlag}
                      alt="destination flag"
                      className="w-10 h-7 object-cover rounded shadow-sm shrink-0"
                    />
                  )}
                </div>
              </div>

              {/* Info chips */}
              <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                {selectedItem?.flightItineraries?.[0]?.ailinelogoUrl && (
                  <img
                    src={selectedItem.flightItineraries[0].ailinelogoUrl}
                    alt="airline"
                    className="h-4 w-auto object-contain"
                  />
                )}
                {[
                  selectedItem?.flightItineraries?.[0]?.carrierName,
                  selectedItem?.flightItineraries?.[0]?.carr_name,
                  formatFlightNo(
                    selectedItem?.flightItineraries?.[0]?.carrierCode,
                    selectedItem?.flightItineraries?.[0]?.carrierNumber
                  ),
                  `⏱ ${MinutesToHours(selectedItem?.travelTime)}`,
                  `LAT: ${formatLatToa(selectedItem?.LAT ?? selectedItem?.lat ?? lattaoData?.lat)}`,
                  `TOA: ${formatLatToa(selectedItem?.TOA ?? selectedItem?.tao ?? lattaoData?.tao)}`,
                  // ...(selectedItem?.ratesFrom === "storedrate"
                  //   ? [
                  //       `Lat: ${selectedItem?.lat ?? "N/A"}`,
                  //       `Tao: ${selectedItem?.tao ?? "N/A"}`,
                  //     ]
                  //   : []),
                  // ...(selectedItem?.ratesFrom === "airlineRates" &&
                  // selectedItem?.flightItineraries[0]?.carrierCode === "QR"
                  //   ? [
                  //       `Lat: ${lattaoData?.lat ?? "N/A"}`,
                  //       `Tao: ${lattaoData?.tao ?? "N/A"}`,
                  //     ]
                  //   : []),
                ]
                  .filter(Boolean)
                  .map((chip, i) => (
                    <span
                      key={i}
                      className="bg-white/70 border border-[#e8d88a] text-gray-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap"
                    >
                      {chip}
                    </span>
                  ))}
                <span className="bg-white/70 border border-[#e8d88a] text-gray-700 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1.5 whitespace-nowrap">
                  <CategoryIcon sx={{ fontSize: 13, color: '#EFA603' }} />
                  {gdCommodityDescription  || "General Cargo"}
                </span>
                {/* Carrier commodity code from the commodity master. */}
                {gdCommodityCode && (
                  <span className="bg-white/70 border border-[#e8d88a] text-gray-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                    Commodity Code: {gdCommodityCode}
                  </span>
                )}
                {/* Temperature range chip — only for temperature-controlled
                    commodities, where the search form published a range. */}
                {gdTempRange && (
                  <span className="bg-white/70 border border-[#e8d88a] text-gray-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1.5 whitespace-nowrap">
                    <AcUnitIcon sx={{ fontSize: 13, color: '#0ea5e9' }} />
                    {gdTempRange}
                  </span>
                )}
                {gdProductCode && (
                  <span className="bg-white/70 border border-[#e8d88a] text-gray-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                    Product: {gdProductCode}
                  </span>
                )}
                {gdShc && (
                  <span className="bg-white/70 border border-[#e8d88a] text-gray-700 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">
                    SHC: {gdShc}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Flight Details Card */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h4 className="text-base font-bold mb-4 text-gray-800">
              Flight Details
            </h4>
            {selectedItem?.flightItineraries?.map((item, index) => (
              <div key={index}>
                <div className="grid grid-cols-3 items-start gap-2">
                  <div>
                    <p className="text-xl font-bold text-gray-800">
                      {item.segmentOfDeparture}
                    </p>
                    <p className="text-sm text-gray-600">
                      {moment(item.scheduledDepartureTime).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </p>
                    <p className="text-xs text-gray-400">
                      {getAirportNameByCode(item.segmentOfDeparture)}
                    </p>
                  </div>
                  <div className="flex flex-col items-center justify-center pt-1">
                    <span className="text-gray-400 text-base mb-0.5">✈</span>
                    <span className="text-xs font-medium text-gray-500">
                      {formatFlightNo(item?.carrierCode, item?.carrierNumber)}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-800">
                      {item.segmentOfArrival}
                    </p>
                    <p className="text-sm text-gray-600">
                      {moment(item.scheduledArrivalTime).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </p>
                    <p className="text-xs text-gray-400">
                      {getAirportNameByCode(item.segmentOfArrival)}
                    </p>
                  </div>
                </div>
                {index < selectedItem?.flightItineraries.length - 1 && (
                  <hr className="my-3 border-gray-100" />
                )}
              </div>
            ))}
          </div>

          {/* Payment Summary + Goods Description */}
          <div className="grid grid-cols-2 gap-4">
            {/* Payment Summary */}
            <div className="rounded-xl border border-[#F5D98A] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-[#FFF9E6] border-b border-[#F5D98A]">
                <h3 className="text-base font-bold text-gray-800">Payment Summary</h3>
                <span className="bg-[#EFA603] text-white text-xs font-bold px-3 py-1 rounded-full">ALL IN</span>
              </div>

              <div className="bg-white px-4 pt-3 pb-2 space-y-3">
                {hasBreakdown ? (
                  <>
                    {/* Air Freight */}
                    {mainCharges.length > 0 && (
                      <div className="border-l-4 border-[#1976D2] rounded-r-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-[#E3F2FD]">
                          <span className="font-bold text-[#1976D2] text-sm">Air Freight</span>
                          <span className="font-bold text-[#1976D2] text-sm">{fmtAmt(mainTotal)}</span>
                        </div>
                        {mainCharges.map((c, i) => (
                          <div key={i} className="flex items-start justify-between px-3 py-2 bg-white border-t border-[#E3F2FD]">
                            <div>
                              <p className="text-sm text-gray-600">{chargeLabel(c)}</p>
                              <p className="text-xs text-gray-400">{fmtKg(c.ratePerkilo)}</p>
                            </div>
                            <span className="text-sm text-[#1976D2] font-medium whitespace-nowrap ml-3">{fmtAmt(getAmt(c))}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Surcharges label */}
                    {fuelSecCharges.length > 0 && (
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider pt-1">Surcharges</p>
                    )}

                    {/* Fuel & Security — always included */}
                    {fuelSecCharges.length > 0 && (
                      <div className="border-l-4 border-[#4CAF50] rounded-r-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-[#E8F5E9]">
                          <span className="font-bold text-[#2E7D32] text-sm">Fuel & Security</span>
                          <span className="font-bold text-[#2E7D32] text-sm">{fmtAmt(fuelSecTotal)}</span>
                        </div>
                        {fuelSecCharges.map((c, i) => (
                          <div key={i} className="flex items-start justify-between px-3 py-2 bg-white border-t border-[#E8F5E9]">
                            <div>
                              <p className="text-sm text-gray-600">{chargeLabel(c)}</p>
                              <p className="text-xs text-gray-400">{fmtKg(c.ratePerkilo)}</p>
                            </div>
                            <span className="text-sm text-[#2E7D32] font-medium whitespace-nowrap ml-3">{fmtAmt(getAmt(c))}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Other Fees — always shown when present */}
                    {showOtherFees && (
                      <div className="border-l-4 border-gray-300 rounded-r-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                          <span className="font-bold text-gray-700 text-sm">Other Fees</span>
                          <span className="font-bold text-gray-700 text-sm">{fmtAmt(otherTotal)}</span>
                        </div>
                        {otherCharges.map((c, i) => (
                          <div key={i} className="flex items-start justify-between px-3 py-2 bg-white border-t border-gray-100">
                            <div>
                              <p className="text-sm text-gray-600">{chargeLabel(c)}</p>
                              <p className="text-xs text-gray-400">{fmtKg(c.ratePerkilo)}</p>
                            </div>
                            <span className="text-sm text-gray-700 font-medium whitespace-nowrap ml-3">{fmtAmt(getAmt(c))}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Screening — always shown when present */}
                    {showScreening && (
                      <div className="border-l-4 border-gray-300 rounded-r-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                          <span className="font-bold text-gray-700 text-sm">Screening</span>
                          <span className="font-bold text-gray-700 text-sm">{fmtAmt(screeningTotal)}</span>
                        </div>
                        {screeningCharges.map((c, i) => (
                          <div key={i} className="flex items-start justify-between px-3 py-2 bg-white border-t border-gray-100">
                            <div>
                              <p className="text-sm text-gray-600">{chargeLabel(c)}</p>
                              <p className="text-xs text-gray-400">{fmtKg(c.ratePerkilo)}</p>
                            </div>
                            <span className="text-sm text-gray-700 font-medium whitespace-nowrap ml-3">{fmtAmt(getAmt(c))}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Other Charges — any category outside the known groups, always shown */}
                    {showMisc && (
                      <div className="border-l-4 border-gray-300 rounded-r-lg overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
                          <span className="font-bold text-gray-700 text-sm">Other Charges</span>
                          <span className="font-bold text-gray-700 text-sm">{fmtAmt(miscTotal)}</span>
                        </div>
                        {miscCharges.map((c, i) => (
                          <div key={i} className="flex items-start justify-between px-3 py-2 bg-white border-t border-gray-100">
                            <div>
                              <p className="text-sm text-gray-600">{chargeLabel(c)}</p>
                              <p className="text-xs text-gray-400">{fmtKg(c.ratePerkilo)}</p>
                            </div>
                            <span className="text-sm text-gray-700 font-medium whitespace-nowrap ml-3">{fmtAmt(getAmt(c))}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Taxable Amount — sum of all charges above, before GST */}
                    <div className="mt-1 border-l-4 border-amber-300 rounded-r-lg overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-amber-50">
                        <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Taxable Amount</span>
                        <span className="text-sm font-bold text-amber-700">{fmtAmt(subtotalAmount)}</span>
                      </div>
                    </div>

                    {/* GST Amount — 18% applied on the taxable amount above */}
                    {gstAmount > 0 && (
                      <div className="border-l-4 border-purple-400 rounded-r-lg overflow-hidden">
                        <div className="flex items-start justify-between px-3 py-2 bg-purple-50">
                          <div>
                            <p className="font-bold text-purple-700 text-sm">GST Amount (18%)</p>
                            <p className="text-xs text-purple-400">on taxable amount {fmtAmt(subtotalAmount)}</p>
                          </div>
                          <span className="font-bold text-purple-700 text-sm whitespace-nowrap ml-3">{fmtAmt(gstAmount)}</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Non-FS or no breakdown: basic list */
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Airfreight Rate</span>
                      <span className="text-sm text-gray-800">
                        {formatrates(selectedItem?.rateDetails?.ratePerkilo?.toFixed(2)) + "/kg"}
                      </span>
                    </div>
                    {selectedItem?.ratesFrom === "storedrate" &&
                      (() => {
                        const bd = selectedItem?.rateDetails?.chargesBreakDown || [];
                        const freightCharge = bd.find(c => c.chargeType === "Freight Charges");
                        const surcharges = bd.filter(c => c.chargeType === "Surcharge");
                        const surchargesTotal = surcharges.reduce((sum, c) => sum + (c.chargeAmount || 0), 0);
                        return (
                          <>
                            {freightCharge && (
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Freight Charges</span>
                                <span className="text-sm text-gray-800">{formatrates(freightCharge.chargeAmount?.toFixed(2))}</span>
                              </div>
                            )}
                            {surcharges.length > 0 && (
                              <div className="rounded-lg border border-gray-100">
                                <div className="flex justify-between items-center p-2 cursor-pointer" onClick={() => setSurchargesExpanded(p => !p)}>
                                  <span className="text-sm font-medium text-gray-600">Surcharges</span>
                                  <span className="text-sm flex items-center gap-1 text-gray-800">
                                    {formatrates(surchargesTotal?.toFixed(2))}
                                    <span className="ml-1 text-xs">{surchargesExpanded ? "∧" : "∨"}</span>
                                  </span>
                                </div>
                                {surchargesExpanded && (
                                  <div className="px-2 pb-2 space-y-1">
                                    {surcharges.map((charge, idx) => (
                                      <div key={idx} className="flex justify-between">
                                        <span className="text-xs text-gray-400">{charge.chargeDescription}:</span>
                                        <span className="text-xs text-gray-400">{formatrates(charge.chargeAmount?.toFixed(2))}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    {/* Every other rate source (QR/EK/direct-airline): list each
                        chargesBreakDown line as it arrives. Field names differ
                        per source, hence the fallbacks for label and amount. */}
                    {selectedItem?.ratesFrom !== "storedrate" &&
                      chargesBreakDown.map((c, i) => (
                        <div key={i} className="flex items-start justify-between">
                          <div>
                            <p className="text-sm text-gray-500">
                              {chargeLabel(c)}
                            </p>
                            {Number(c?.ratePerkilo) > 0 && (
                              <p className="text-xs text-gray-400">{fmtKg(c.ratePerkilo)}</p>
                            )}
                          </div>
                          <span className="text-sm text-gray-800 whitespace-nowrap ml-3">
                            {fmtAmt(getAmt(c))}
                          </span>
                        </div>
                      ))}
                    {viewBreakupData?.pickupCharges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Pickup Charges</span>
                        <span className="text-sm text-gray-800">{formatrates(viewBreakupData?.pickupCharges?.toFixed(2))}</span>
                      </div>
                    )}
                    {viewBreakupData?.deliveryCharges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Delivery Charges</span>
                        <span className="text-sm text-gray-800">{formatrates(viewBreakupData?.deliveryCharges?.toFixed(2))}</span>
                      </div>
                    )}
                    {viewBreakupData?.OriginCharges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Origin Charges</span>
                        <span className="text-sm text-gray-800">{formatrates(viewBreakupData?.OriginCharges?.toFixed(2))}</span>
                      </div>
                    )}
                    {viewBreakupData?.DestinationCharges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Destination Charges</span>
                        <span className="text-sm text-gray-800">{formatrates(viewBreakupData?.DestinationCharges?.toFixed(2))}</span>
                      </div>
                    )}
                    {viewBreakupData?.vas > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">VAS Charges</span>
                        <span className="text-sm text-gray-800">{formatrates(viewBreakupData?.vas?.toFixed(2))}</span>
                      </div>
                    )}
                    {/* No border-l-4 accent here (unlike the grouped-breakdown view above) —
                        this list's rows are plain, unboxed "flex justify-between" text, so
                        Taxable Amount/GST Amount use the -mx-4/px-4 bleed trick to keep their
                        text flush with those rows while still standing out with a full-width
                        background tint, instead of the extra indent a bordered card would add. */}
                    <div className="-mx-4 px-4 mt-1 py-2 flex items-center justify-between bg-amber-50">
                      <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Taxable Amount</span>
                      <span className="text-sm font-bold text-amber-700">{formatrates(subtotalAmount.toFixed(2))}</span>
                    </div>
                    {gstAmount > 0 && (
                      <div className="-mx-4 px-4 py-2 flex items-start justify-between bg-purple-50">
                        <div>
                          <p className="font-bold text-purple-700 text-sm">GST Amount (18%)</p>
                          <p className="text-xs text-purple-400">on taxable amount {formatrates(subtotalAmount.toFixed(2))}</p>
                        </div>
                        <span className="font-bold text-purple-700 text-sm whitespace-nowrap ml-3">{formatrates(gstAmount.toFixed(2))}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Net Amount footer — Taxable Amount + GST Amount */}
              <div className="flex justify-between items-center px-4 py-3 bg-[#FFF9E6] border-t border-[#F5D98A]">
                <span className="text-sm font-bold text-gray-800">Net Amount</span>
                <span className="text-base font-bold text-[#EFA603]">{fmtAmt(totalAmount)}</span>
              </div>

              {/* Update flow: previously paid / amount due */}
              {isUpdateFlow && (
                <div className="px-4 pb-3 bg-[#FFF9E6] space-y-1 border-t border-[#F5D98A]">
                  <div className="flex justify-between pt-2">
                    <span className="text-sm text-gray-500">Previously Paid</span>
                    <span className="text-sm text-gray-800">{formatrates(Number(prevBookingAmount).toFixed(2))}</span>
                  </div>
                  {noPaymentNeeded ? (
                    <div className="flex justify-between">
                      <span className="text-sm font-semibold text-green-700">Balance (no charge)</span>
                      <span className="text-sm font-bold text-green-600">{formatrates((prevBookingAmount - totalAmount).toFixed(2))}</span>
                    </div>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-sm font-semibold text-orange-700">Amount Due</span>
                      <span className="text-sm font-bold text-orange-600">{formatrates(differenceAmount.toFixed(2))}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Goods Description */}
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-base font-bold mb-3 text-gray-800">
                Goods Description
              </h3>
              <div className="text-xs p-2 rounded-md text-center text-white bg-[#f1b21b] mb-3">
                To be confirmed by the handling agent at the airport
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Nature of Goods</span>
                  <span className="text-sm text-gray-800">
                    {gdCommodityDescription || newsearchformdata?.searchquery?.goodsDetails?.cargoType || "General Cargo"}
                  </span>
                </div>
                {/* Carrier codes from the commodity master — each row is
                    skipped when the commodity has no such code. */}
                {gdCommodityCode && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Commodity Code</span>
                    <span className="text-sm text-gray-800">{gdCommodityCode}</span>
                  </div>
                )}
                {gdProductCode && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Product Code</span>
                    <span className="text-sm text-gray-800">{gdProductCode}</span>
                  </div>
                )}
                {gdShc && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Special Handling Codes</span>
                    <span className="text-sm text-gray-800">{gdShc}</span>
                  </div>
                )}
                {gdTempRange && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">
                      Temperature Range
                    </span>
                    <span className="text-sm font-semibold text-gray-800">
                      {gdTempRange}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Quantity</span>
                  <span className="text-sm text-gray-800">
                    {gdQty ? `${gdQty} Units` : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  {/* ── Label switches between "Total CBM" and "Total CFT" based on dim unit ── */}
                  <span className="text-sm text-gray-500">
                    {gdVolUnit === "CFT" ? "Total CFT" : "Total CBM"}
                  </span>
                  <span className="text-sm text-gray-800">
                    {gdVol
                      ? `${Number(gdVol).toFixed(2)} ${gdVolUnit}${gdVolUnit === "CFT" ? ` (${(Number(gdVol) * 0.0283168).toFixed(2)} CBM)` : ""}`
                      : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Gross Weight</span>
                  <span className="text-sm text-gray-800">
                    {gdGW ? `${Number(gdGW).toFixed(2)} ${gdWunit}` : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">
                    Volumetric Weight
                  </span>
                  <span className="text-sm text-gray-800">
                    {gdVW != null ? `${gdVW.toFixed(2)} ${gdWunit}` : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">
                    Chargeable Weight
                  </span>
                  <span className="text-sm font-semibold text-blue-600">
                    {gdCW != null ? `${Math.ceil(Number(gdCW))} ${gdWunit}` : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* AWB Type Selection — QR airline only */}
          {showAwbOptions && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="text-base font-bold mb-3 text-gray-800">AWB Type</h3>
              <div className="flex flex-wrap gap-4">
                {[
                  { value: "Paper AWB", label: "Paper AWB", desc: "Physical paper air waybill" },
                  { value: "EAP", label: "EAP", desc: "Electronic AWB — Prepaid" },
                  { value: "EAW", label: "EAW", desc: "Electronic AWB — Weight" },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 cursor-pointer border rounded-xl px-4 py-3 flex-1 min-w-[140px] transition-colors ${
                      selectedValue === opt.value
                        ? "border-[#f1b21b] bg-[#FFF9E6]"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="awbType"
                      value={opt.value}
                      checked={selectedValue === opt.value}
                      onChange={handleRadioChange}
                      className="mt-0.5 accent-[#f1b21b]"
                    />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Terms & Confirm */}
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-start mb-4">
              <input
                className="mt-1 mr-2 shrink-0"
                type="checkbox"
                id="newFlowTermsCheckbox"
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
              />
              <label
                htmlFor="newFlowTermsCheckbox"
                className="text-xs text-gray-600"
              >
                I confirm that I have read and understood the{" "}
                <span
                  className="text-blue-500 underline cursor-pointer"
                  onClick={() => setIsPopupOpen(true)}
                >
                  Additional Remark
                </span>{" "}
                <a
                  href="https://www.qrcargo.com/s/network-and-services/general-terms-and-conditions"
                  className="text-blue-500"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  General and local terms and condition
                </a>{" "}
                and{" "}
                <a
                  href="https://www.qrcargo.com/s/network-and-services/condition-of-carriage"
                  className="text-blue-500"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  condition of carriage
                </a>{" "}
                that apply to this booking
              </label>
            </div>

            {isSubmitIndent ? (
            <button
              className={`w-full text-center text-white rounded-xl py-3 text-base font-semibold transition-colors flex items-center justify-center gap-2 ${
                isChecked && !isQuoteExpired && !spinner
                  ? "bg-[#f1b21b] hover:bg-[#e0a518]"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
              onClick={handleSubmitIndent}
              disabled={!isChecked || isQuoteExpired || spinner}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {spinner ? "Submitting…" : "Submit Indent"}
            </button>
            ) : (
            <button
              className={`w-full text-center text-white rounded-xl py-3 text-base font-semibold transition-colors flex items-center justify-center gap-2 ${
                isChecked && !isQuoteExpired
                  ? "bg-[#f1b21b] hover:bg-[#e0a518]"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
              onClick={noPaymentNeeded ? handleConfirmUpdate : handleProceedToPay}
              disabled={!isChecked || isQuoteExpired}
            >
              {noPaymentNeeded ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Confirm Update
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path
                      fillRule="evenodd"
                      d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {payDifference
                    ? `Pay Difference — ${formatrates(differenceAmount.toFixed(2))}`
                    : `Proceed to Pay${totalAmount ? ` — ${formatrates(totalAmount.toFixed(2))}` : ""}`}
                </>
              )}
            </button>
            )}
          </div>
        </div>
      </div>

      {/* CashFree Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                {selectedItem?.flightItineraries?.[0]?.ailinelogoUrl && (
                  <img
                    src={selectedItem.flightItineraries[0].ailinelogoUrl}
                    alt="airline"
                    className="h-8 w-auto object-contain"
                  />
                )}
                <div>
                  <h2 className="text-base font-bold text-gray-800">
                    Complete Payment
                  </h2>
                  <p className="text-xs text-gray-500">
                    {or} → {des} &nbsp;|&nbsp;{" "}
                    {selectedItem?.flightItineraries?.[0]?.carrierName}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClosePaymentModal}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
                title="Cancel payment"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Amount banner */}
            <div className="px-6 py-3 bg-[#FFF9E6] border-b border-[#F5DFA0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#f1b21b] flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-white"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path
                      fillRule="evenodd"
                      d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Amount Payable
                </span>
              </div>
              <span className="text-xl font-bold text-[#f1b21b]">
                {amountToPay
                  ? formatrates(Number(amountToPay).toFixed(2))
                  : "—"}
              </span>
            </div>

            {/* Drop-in container */}
            <div className="p-4 overflow-y-auto max-h-[65vh]">
              <div id="new_booking_drop_in" className="min-h-[200px]" />
            </div>

            {/* Security note */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-green-500 shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-xs text-gray-500">
                Your payment is secured by Cashfree Payments. 256-bit SSL
                encryption.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Terms popup */}
      {isPopupOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setIsPopupOpen(false)}
        >
          <div
            className="bg-white p-6 rounded-md shadow-lg w-[500px] h-[400px] max-w-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm mb-2">
              Applicable rates and charges are based on their respective
              validity period and will be invoiced accordingly as per issue date
              registered on the master air waybill document.
            </p>
            <ul className="list-disc list-inside text-sm space-y-2">
              <li>
                The rate may change if any amendment is done on the booking
                through manual or electronic (FWB) means.
              </li>
              <li>
                Shipments containing Dry Ice and Lithium Batteries (ELI/ELM),
                including Overhang Items (OHG) should not be booked on
                marketplaces.
              </li>
              <li>
                Booking cancellation must be performed using the platform
                through which the booking was created.
              </li>
              <li>
                Air Plus Solutions offer additional features for shipments
                booked with compatible products. For added reassurance, Q-Plus
                increases the loading priority of your shipment.
              </li>
              <li>
                Other Charges are indicative at the booking stage, and charges
                related to additional special requirements may not be included.
                Complete list of applicable charges and fees (OCDCs) per country
                due carrier is available on{" "}
                <a
                  href="https://www.qrcargo.com/s/charges-fees"
                  className="text-blue-500"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  QR Cargo website.
                </a>
              </li>
              <li>
                Additional remarks indicated on this platform must be taken in
                conjunction with the{" "}
                <a
                  href="https://www.qrcargo.com/s/network-and-services/general-terms-and-conditions"
                  className="text-blue-500"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  General & Local Terms and Conditions
                </a>{" "}
                that apply to all bookings.
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Payment Confirmed — Booking Underway Modal */}
      <div
        style={{
          transition: "opacity 0.3s ease, transform 0.3s ease",
          opacity: showPaymentPending ? 1 : 0,
          pointerEvents: showPaymentPending ? "auto" : "none",
          transform: showPaymentPending ? "scale(1)" : "scale(0.95)",
        }}
        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200]"
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          {/* Top accent */}
          <div className="h-1.5 w-full bg-gradient-to-r from-yellow-400 to-orange-400" />
          <div className="px-8 py-8 flex flex-col items-center text-center gap-5">
            {/* Animated ring with checkmark */}
            <div className="relative w-20 h-20 flex items-center justify-center">
              <svg className="absolute inset-0 w-20 h-20 animate-spin" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="34" stroke="#f3f4f6" strokeWidth="6" />
                <path
                  d="M40 6 a34 34 0 0 1 34 34"
                  stroke="#f1b21b"
                  strokeWidth="6"
                  strokeLinecap="round"
                />
              </svg>
              <div className="w-12 h-12 rounded-full bg-[#fff8e6] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#f1b21b]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
            </div>

            {/* Text */}
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-1">Payment Confirmed!</h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                Please wait while we confirm your booking.<br />This may take a few seconds.
              </p>
            </div>

            {/* Animated dots */}
            <div className="flex items-center gap-1.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-2 h-2 rounded-full bg-[#f1b21b]"
                  style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Update In Progress Modal (no payment needed) */}
      <div
        style={{
          transition: "opacity 0.3s ease, transform 0.3s ease",
          opacity: showUpdatePending ? 1 : 0,
          pointerEvents: showUpdatePending ? "auto" : "none",
          transform: showUpdatePending ? "scale(1)" : "scale(0.95)",
        }}
        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200]"
      >
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
          <div className="h-1.5 w-full bg-gradient-to-r from-green-400 to-teal-400" />
          <div className="px-8 py-8 flex flex-col items-center text-center gap-5">
            {/* Animated ring with wallet icon */}
            <div className="relative w-20 h-20 flex items-center justify-center">
              <svg className="absolute inset-0 w-20 h-20 animate-spin" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="34" stroke="#f3f4f6" strokeWidth="6" />
                <path
                  d="M40 6 a34 34 0 0 1 34 34"
                  stroke="#22c55e"
                  strokeWidth="6"
                  strokeLinecap="round"
                />
              </svg>
              <div className="w-12 h-12 rounded-full bg-[#f0fdf4] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  <path d="M2 7l10-4 10 4" />
                </svg>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-1">Payment Adjusted</h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                Payment has been adjusted from your previous booking amount.<br />
                Waiting for booking confirmation…
              </p>
            </div>

            <div className="flex items-center gap-1.5">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-2 h-2 rounded-full bg-green-400"
                  style={{ animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Booking Confirmed Modal */}
      {showBookingSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[200]">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            {/* Green header */}
            <div className="relative bg-gradient-to-b from-[#e6f9ee] to-[#f0fdf4] pt-10 pb-6 flex flex-col items-center">
              {/* Confetti dots */}
              <span className="absolute top-4 left-6 w-3 h-3 rounded-full bg-orange-400 opacity-80" />
              <span className="absolute top-8 left-16 w-2 h-2 rounded-full bg-blue-400 opacity-70" />
              <span className="absolute top-4 right-6 w-2.5 h-2.5 rounded-full bg-green-500 opacity-80" />
              <span className="absolute top-10 right-16 w-2 h-2 rounded-full bg-yellow-400 opacity-70" />
              <span className="absolute bottom-8 left-10 w-2 h-2 rounded-full bg-pink-400 opacity-70" />
              <span className="absolute bottom-6 right-10 w-2 h-2 rounded-full bg-blue-500 opacity-60" />
              <span className="absolute top-6 left-1/3 w-1.5 h-1.5 rounded-full bg-yellow-500 opacity-70" />
              <span className="absolute bottom-10 right-1/3 w-1.5 h-1.5 rounded-full bg-green-400 opacity-60" />
              {/* Checkmark circle */}
              <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-extrabold text-green-700 mb-1">Booking Confirmed!</h2>
              <p className="text-sm text-gray-500">Your shipment is all set</p>
            </div>

            {/* AWB + message */}
            <div className="px-6 py-5">
              <div className="border border-gray-200 rounded-xl px-4 py-4 text-center mb-5">
                <p className="text-sm text-green-600 font-medium">Booking confirmed successfully.</p>
              </div>

              {/* Countdown */}
              <div className="flex items-center gap-3 mb-5">
                <div className="relative w-14 h-14 shrink-0">
                  <svg className="w-14 h-14 -rotate-90" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="#e5e7eb" strokeWidth="4" />
                    <circle
                      cx="24" cy="24" r="20" fill="none"
                      stroke="#22c55e" strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray={125.66}
                      strokeDashoffset={125.66 * (1 - countdown / 12)}
                      style={{ transition: "stroke-dashoffset 0.9s linear" }}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-700">{countdown}</span>
                </div>
                <p className="text-sm text-gray-600">
                  Redirecting to dashboard in <strong>{countdown}s</strong>
                </p>
              </div>

              {/* Button */}
              <button
                onClick={() => navigate("/booking-history")}
                className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-white font-bold py-3 rounded-xl text-base transition-all"
              >
                Redirect Now
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default NewBookingFlowContent;
